import { useQuery } from '@tanstack/react-query'
import accountingService from '@/services/accountingService'
import { useApiReady } from '@/hooks/useApiReady'

// Query keys
export const accountingEntriesKeys = {
  all: ['accountingEntries'] as const,
  lists: () => [...accountingEntriesKeys.all, 'list'] as const,
  list: (filters: any) => [...accountingEntriesKeys.lists(), filters] as const,
}

interface AccountingEntriesFilters {
  status?: string
  type?: string
  search?: string
  freelancer?: string
  period?: string
  /** `sent` | `not_sent` */
  accounting_send?: string
  /** `exported` | `not_exported` (équipe Napta) */
  napta_export?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  team_id?: string
  umbrella?: 'yes' | 'no'
}

/**
 * Hook pour récupérer les entrées comptables
 */
export function useAccountingEntries(filters: AccountingEntriesFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: accountingEntriesKeys.list(filters),
    queryFn: async () => {
      const { success, data: entries, total: totalCount } = await accountingService.getEntries({
        status: filters.status !== 'all' ? filters.status : undefined,
        type: filters.type !== 'all' ? filters.type : undefined,
        search: filters.search?.trim() || undefined,
        freelancer: filters.freelancer !== 'all' ? filters.freelancer : undefined,
        period: filters.period || undefined,
        accounting_send: filters.accounting_send || undefined,
        napta_export: filters.napta_export || undefined,
        page: filters.page,
        pageSize: filters.pageSize,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        team_id: filters.team_id || undefined,
        umbrella: filters.umbrella || undefined,
      })

      if (!success) {
        throw new Error('Failed to load accounting entries')
      }

      if (!Array.isArray(entries)) {
        return { entries: [], total: 0 }
      }

      return { entries, total: totalCount ?? entries.length }
    },
    enabled: apiReady && options?.enabled !== false,
  })
}
